import moment from "moment";

// Build Your Own Package — placeholder configuration.
//
// TODO(api): tiers, plan terms and FAQ copy should come from the API once the
// package products exist server-side. Everything here is design-phase content
// so the front-end flow can be exercised end to end.

// Hours are chosen freely in 10-hour steps. Pricing is a per-clock-hour
// split: the upfront share (bundle cost) is due at enrollment — payable in
// full or financed on the standard payment agreement — and the deferred
// share (clock hour fee) is billed as courses are completed.
// Every split totals the SAME $8/hr (standardized 2026-08-28): the choice
// only moves money between the bundle cost and the pay-as-you-complete
// clock hour fees, it never changes what the bundle costs overall.
export const SPLIT_OPTIONS = [
    {upfront: 5, deferred: 3},
    {upfront: 4, deferred: 4, standard: true},
    {upfront: 3, deferred: 5}
];
export const DEFAULT_SPLIT = SPLIT_OPTIONS.find(o => o.standard);
export const ALACARTE_RATE = 12;   // $ per clock hour, enrolling course by course

export function splitByUpfront(rate) {
    return SPLIT_OPTIONS.find(o => o.upfront === rate) || DEFAULT_SPLIT;
}
export const HOURS_MIN = 150;      // smallest package we sell
// The largest package we sell in one purchase; column moves needing more are
// bought in installments
export const HOURS_MAX = 700;
export const HOURS_STEP = 10;

// Standard payment agreement defaults: monthly payments always start on
// October 1 — when most districts' scale-advancement raises take effect
// (the earn-now-pay-later concept). Hours are assumed earned at
// PACE_HOURS_PER_MONTH; a bundle that can't plausibly finish before the
// coming October 1 targets the following one instead.
export const PACE_HOURS_PER_MONTH = 50;
export const TERM_MONTHS = 12;

// First payment date for a financed bundle (moment). Must mirror the API's
// planDeferOn() in packages.controller.js.
export function firstPaymentDateFor(hours) {
    const today = moment();
    const target = moment(today.format('YYYY') + '-10-01', 'YYYY-MM-DD');
    if (!today.isBefore(target)) target.add(1, 'year');
    if ((hours || 0) / PACE_HOURS_PER_MONTH > target.diff(today, 'months')) target.add(1, 'year');
    return target;
}

export function upfrontForHours(hours, rate = DEFAULT_SPLIT.upfront) {
    return hours * rate;
}

export function deferredForHours(hours, rate = DEFAULT_SPLIT.upfront) {
    return hours * splitByUpfront(rate).deferred;
}

export function priceForHours(hours, rate = DEFAULT_SPLIT.upfront) {
    const split = splitByUpfront(rate);
    return hours * (split.upfront + split.deferred);
}

export function alacarteForHours(hours) {
    return hours * ALACARTE_RATE;
}

export function discountForHours(hours, rate = DEFAULT_SPLIT.upfront) {
    if (!hours) return 0;
    return (alacarteForHours(hours) - priceForHours(hours, rate)) / alacarteForHours(hours);
}

// Snap any hour value onto the step grid within bounds (0 = not chosen yet)
export function roundHours(hours) {
    const rounded = Math.round((parseInt(hours) || 0) / HOURS_STEP) * HOURS_STEP;
    return Math.max(0, Math.min(HOURS_MAX, rounded));
}

// The standard payment agreement (executed by the payment partner) runs the
// full package term — 12 monthly payments starting October 1
export const PLAN = {
    months: TERM_MONTHS
};

// Allocation granularity for spending package hours on a course
export const COURSE_HOUR_OPTIONS = [10, 20, 30, 40, 50];

// The package catalog is limited to this certification's courses; most of
// them can be enrolled in repeatedly (with unique names)
export const PACKAGE_CERT_ID = 1;
export const MAX_INSTANCES_PER_COURSE = 3;

// A course belongs to the package catalog via its categories or configs
export function isPackageCourse(course) {
    return (course.categories || []).some(cat => cat.certification_id === PACKAGE_CERT_ID)
        || (course.configs || []).some(cfg => cfg.certification_id === PACKAGE_CERT_ID);
}

// The most bundle hours a course can be allocated: claimable clock hours are
// capped by the enrollment's config units at application time, so allocations
// are limited to the course's largest package-certification config
// (units 0/null = uncapped)
export function packageHourCap(course) {
    const caps = (course.configs || [])
        .filter(cfg => cfg.certification_id === PACKAGE_CERT_ID)
        .map(cfg => cfg.units || null);
    if (!caps.length || caps.some(cap => cap === null)) return Infinity;
    return Math.max(...caps);
}

export const LEAD_TIMES = ['Morning', 'Afternoon', 'Evening'];

export const ENTRY_FAQ = [
    {q: 'What is a bundle?', a: 'A bundle of clock hours you buy once at a discounted rate, then spend across any courses in the catalog — 33% less than enrolling course by course. Bundles start at a minimum of 150 clock hours and go up to a maximum of 700. Need more than 700? Start with a smaller bundle now and build another once it wraps up, or work with a representative.'},
    {q: 'How does payment work?', a: 'Your bundle has two parts. The bundle cost is due at enrollment and is non-refundable — pay it today, or finance it with a Deferred Payment Agreement through our lending partner, The Payment Option: nothing due today, with monthly payments that don\'t begin until October 1, when most scale-advancement raises take effect. Clock hour fees are the pay-as-you-go part — only ever charged for hours you actually use, billed as you complete your courses. Hours you never use are never billed.\n\nPrefer to finance your entire bundle? Work with a representative — they can arrange financing for the full amount, clock hour fees included.'},
    {q: 'What does a representative do?', a: 'They help you choose the right hours and courses, handle enrollment for you, and stay on as your direct support contact for your professional development needs.'},
    {q: 'Are these hours state-approved?', a: 'Yes. Washington State Approved Clock Hours are awarded for all courses. Apply for your earned hours once you complete course requirements — the official FORM SPI 1125 will be made available with the approved provider information that awards the clock hours.'}
];

export const FAQ_BY_STEP = {
    1: [
        {q: 'Why do you ask about my district and column?', a: 'Each district has its own salary schedule, and where you sit on it determines how many clock hours actually move you to the next column. Confirming this helps us size your bundle — and keeps your records accurate.'},
        {q: 'How many clock hours do I really need?', a: 'It depends on your goal. Moving up your salary schedule takes the hours between your current column and the one you\'re aiming for — the options above are sized to exactly that — while certificate renewal typically takes 100 clock hours every five years. Before you settle on a number, check with your district on how many hours you already have banked, and consider anything coming up — conferences, trainings, or other events — that could add hours toward the same goal.'},
        {q: 'What if I need more hours later?', a: 'Individual courses are always available à la carte in the Online Course Catalog, and you can build a new bundle once your current one wraps up — only one financed bundle can be active at a time. For special situations, a representative may be able to help.'},
        {q: 'When do my hours expire?', a: 'Courses in your bundle are valid for 3 years from enrollment.'},
        {q: 'Are these hours state-approved?', a: 'Yes. Washington State Approved Clock Hours are awarded for all courses. Apply for your earned hours once you complete course requirements — the official FORM SPI 1125 will be made available with the approved provider information that awards the clock hours.'}
    ],
    2: [
        {q: 'How are these areas used?', a: 'They only decide which courses we recommend at the top of the next step. You can still browse the full catalog and choose any course.'},
        {q: 'Can I change these answers later?', a: 'Yes. Use the Back button any time before enrolling — your course selections are kept.'}
    ],
    3: [
        {q: 'Can I take the same course more than once?', a: 'Yes — up to three times. Give each enrollment its own focus, and take a different angle through the material each pass.'},
        {q: 'Why does each enrollment need a unique name?', a: 'Every enrollment appears on your records and completion forms by name, so repeats need a focus that sets them apart — including from courses you already have.'},
        {q: 'Do I have to allocate every hour now?', a: 'Yes — every hour is assigned to a course at checkout. Unsure about a course? No problem: any hours you haven\'t logged yet can be reallocated to other courses later from the course\'s Your Enrollment page.'},
        {q: 'Can I split hours across many courses?', a: 'Yes. Add as many courses as you like in 10-hour increments until your bundle is fully allocated.'},
        {q: 'What if a course is not listed here?', a: 'This list shows courses eligible for bundle hours. Everything else remains available à la carte in the Online Course Catalog.'}
    ],
    4: [
        {q: 'How do the costs work?', a: 'Your bundle has two parts. The bundle cost secures your hours and courses — it\'s due at enrollment, paid today or through a payment plan, and is non-refundable. Clock hour fees are the pay-as-you-go part: they\'re only ever charged for hours you actually use, billed as you complete your courses. Hours you never use are never billed — the bundle cost is the only amount you\'re committed to.'},
        {q: 'Why would I choose one split over another?', a: 'The total is the same either way — $8 per clock hour — so it simply comes down to how you\'d like to pay. A higher bundle cost now means less owed along the way as you complete courses. A lower bundle cost keeps more money in your pocket today, with more of your bundle billed as clock hour fees — and those are only ever billed for hours you actually complete. Choose the balance that fits your budget and your plans.'},
        {q: 'Why does my first payment start October 1?', a: 'That\'s when most districts\' scale-advancement pay raises take effect. Earn your clock hours now, move up your salary schedule, and your raise arrives before your first payment does. Larger bundles that can\'t reasonably be finished before the coming October start the following October 1 instead. Keep in mind a raise requires completing your clock hours and processing by your district before it can take effect.'},
        {q: 'What are the payment plan terms?', a: 'The full terms of your Deferred Payment Agreement — payment schedule, early payoff, and everything else — are set out in your contract with our lending partner, The Payment Option, which you review and sign during plan setup.'},
        {q: 'Who handles my payment details?', a: 'Our lending partner, The Payment Option. Your payment details are never stored on my247ed.com.'}
    ],
    5: [
        {q: 'When can I start my courses?', a: 'Immediately after enrolling. Your courses appear under Course Enrollments and hours are available to log right away.'},
        {q: 'Can I change my courses after I enroll?', a: 'Yes. Open the course, choose Your Enrollment, and use Reallocate Hours to move any hours you haven\'t logged yet into one or more other courses. Hours you have already logged stay where they are, and a reallocation cannot be reversed.'}
    ]
};
